import React, { createContext, useContext, useReducer, ReactNode, useCallback, useRef, useEffect } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useUser } from '@clerk/clerk-react';
import { useMutation, useQuery } from 'convex/react';
import { api } from '../convex/_generated/api';
import { Id } from '../convex/_generated/dataModel';
import { AppState, JobStatus, CategoryMode } from '../types';
// MIGRATION: Commented out apiService - now using Convex-only architecture
// import { apiService } from '../services/apiService';
import { useConvexUploadService } from '../services/convexUploadService';
import { analytics } from '../services/analytics';
import { validateFileSize } from '../utils/fileValidation';
import { useToast } from './ToastContext';
import { useCredits } from '../hooks/useCredits';
import {
  validateSubmission,
  filterOversizedFiles,
  checkLimits,
  prepareCustomCategories,
  formatRejectedFilesMessage,
  getRejectionErrorMessage,
  handleLimitExceeded,
} from '../utils/jobSubmission';

/**
 * Enhanced App Context - Combines file queue, email state, and job management
 * 
 * This context provides centralized state management for:
 * - File queue operations (add, remove, clear files)
 * - User email validation and persistence
 * - Job submission and status tracking
 * - LocalStorage persistence for user preferences
 * 
 * Modern patterns:
 * - useReducer for complex state management
 * - useCallback for optimized event handlers
 * - TypeScript strict typing
 * - Separation of concerns
 */

// Extended app state to include email, categories, animations, HEIC conversion, and purchase modal
interface ExtendedAppState extends AppState {
  // Animation states for progressive UX
  shouldBounceEmail: boolean;
  shouldHighlightCategories: boolean;
  shouldPulseSubmit: boolean;
  // HEIC conversion settings
  convertHeicAndHeif: boolean;
  hasHeicFiles: boolean;
  // Purchase credits modal
  showPurchaseCreditsModal: boolean;
  purchaseModalData: {
    fileCount: number;
    creditsNeeded: number;
    totalSizeBytes: number;
  } | null;
}

/**
 * Action types for AppContext reducer
 *
 * Follows TypeScript discriminated union pattern for type-safe actions.
 * Each action has a unique 'type' field and optional 'payload'.
 *
 * @example
 * ```typescript
 * dispatch({ type: 'ADD_FILES', payload: files });
 * dispatch({ type: 'SET_EMAIL', payload: { email: 'user@example.com', isValid: true } });
 * dispatch({ type: 'CLEAR_QUEUE' }); // No payload
 * ```
 */
export type ExtendedActions =
  | { type: 'ADD_FILES'; payload: File[] }
  | { type: 'REMOVE_FILE'; payload: string }
  | { type: 'CLEAR_QUEUE' }
  | { type: 'SET_JOB_STATUS'; payload: JobStatus | null }
  | { type: 'SET_SUBMITTING'; payload: boolean }
  | { type: 'SET_ERROR'; payload: string | null }
  | { type: 'CLEAR_JOB' }
  | { type: 'SET_EMAIL'; payload: { email: string; isValid: boolean } }
  | { type: 'SET_CATEGORIES'; payload: { categories: string[]; mode: CategoryMode; isValid: boolean } }
  | { type: 'RESTORE_USER_PREFERENCES' }
  | { type: 'TRIGGER_EMAIL_BOUNCE' }
  | { type: 'STOP_EMAIL_BOUNCE' }
  | { type: 'TRIGGER_CATEGORY_HIGHLIGHT' }
  | { type: 'STOP_CATEGORY_HIGHLIGHT' }
  | { type: 'TRIGGER_SUBMIT_PULSE' }
  | { type: 'STOP_SUBMIT_PULSE' }
  | { type: 'SET_HEIC_CONVERSION'; payload: boolean }
  | { type: 'UPDATE_HEIC_FILES_DETECTION'; payload: boolean }
  | { type: 'SHOW_PURCHASE_MODAL'; payload: { fileCount: number; creditsNeeded: number; totalSizeBytes: number } }
  | { type: 'HIDE_PURCHASE_MODAL' };

// LocalStorage keys
const STORAGE_KEYS = {
  CURRENT_JOB: 'sortingcat_current_job',
  JOB_HISTORY: 'sortingcat_job_history',
  USER_EMAIL: 'sortingcat_user_email',
  USER_PREFERENCES: 'sortingcat_user_preferences',
  CATEGORY_MODE: 'sortingcat_category_mode',
  CUSTOM_CATEGORIES: 'sortingcat_custom_categories',
  HEIC_CONVERSION: 'sortingcat_heic_conversion',
};

// Enhanced reducer with email handling
const appReducer = (state: ExtendedAppState, action: ExtendedActions): ExtendedAppState => {
  switch (action.type) {
    case 'SET_EMAIL': {
      // Note: Email is NOT persisted to storage for privacy
      const newState = {
        ...state,
        email: action.payload.email,
        isEmailValid: action.payload.isValid,
      };

      // Auto-trigger category highlight when email becomes valid
      if (action.payload.isValid && !state.isEmailValid) {
        newState.shouldBounceEmail = false;
        newState.shouldHighlightCategories = true;
      }

      return newState;
    }

    case 'SET_CATEGORIES': {
      // Persist categories to localStorage
      try {
        localStorage.setItem(STORAGE_KEYS.CATEGORY_MODE, action.payload.mode);
        localStorage.setItem(STORAGE_KEYS.CUSTOM_CATEGORIES, JSON.stringify(action.payload.categories));
      } catch (error) {
        console.warn('Failed to save categories to localStorage:', error);
      }

      const categoryState = {
        ...state,
        categoryMode: action.payload.mode,
        customCategories: action.payload.categories,
        areCategoriesValid: action.payload.isValid,
      };

      // Auto-trigger submit pulse when categories are set and email is valid
      if (action.payload.isValid && state.isEmailValid) {
        categoryState.shouldHighlightCategories = false;
        categoryState.shouldPulseSubmit = true;
      }

      return categoryState;
    }

    case 'RESTORE_USER_PREFERENCES':
      try {
        // Note: Email is NOT restored from storage for privacy
        const savedCategoryMode = localStorage.getItem(STORAGE_KEYS.CATEGORY_MODE) as CategoryMode || 'predefined';
        const savedCustomCategories = JSON.parse(localStorage.getItem(STORAGE_KEYS.CUSTOM_CATEGORIES) || '[]');
        const savedHeicConversion = localStorage.getItem(STORAGE_KEYS.HEIC_CONVERSION);
        const convertHeicAndHeif = savedHeicConversion !== null ? savedHeicConversion === 'true' : true; // Default true
        return {
          ...state,
          email: '',
          isEmailValid: false,
          categoryMode: savedCategoryMode,
          customCategories: savedCustomCategories,
          areCategoriesValid: true, // Categories are always considered valid for UX
          shouldBounceEmail: false,
          shouldHighlightCategories: false,
          shouldPulseSubmit: false,
          convertHeicAndHeif,
        };
      } catch (error) {
        console.warn('Failed to restore user preferences:', error);
        return state;
      }

    // Delegate file queue actions to original reducer logic
    case 'ADD_FILES': {
      // Validate files and only add valid ones to the queue
      const validatedFiles = action.payload.map((file: File) => ({
        id: `${Date.now()}-${Math.random()}`,
        file,
        addedAt: new Date(),
        type: getFileType(file),
        size: file.size,
        validation: validateFileSize(file),
      }));

      // Only add files that pass validation (Decision #3: Option B - valid files continue)
      const validFiles = validatedFiles.filter(f => f.validation.isValid);

      const newFileState = {
        ...state,
        fileQueue: [
          ...state.fileQueue,
          ...validFiles,
        ],
      };

      // Auto-trigger email bounce if email is not valid and files were added
      if (!state.isEmailValid && validFiles.length > 0) {
        newFileState.shouldBounceEmail = true;
      }

      // Check for HEIC files in new files and update detection
      const allFilesIncludingNew = [...state.fileQueue.map(f => f.file), ...validFiles.map(f => f.file)];
      newFileState.hasHeicFiles = hasHeicFiles(allFilesIncludingNew);

      return newFileState;
    }

    case 'REMOVE_FILE': {
      const filteredQueue = state.fileQueue.filter(f => f.id !== action.payload);
      return {
        ...state,
        fileQueue: filteredQueue,
        hasHeicFiles: hasHeicFiles(filteredQueue.map(f => f.file)),
      };
    }

    case 'CLEAR_QUEUE':
      return {
        ...state,
        fileQueue: [],
        hasHeicFiles: false,
      };

    case 'SET_SUBMITTING':
      return {
        ...state,
        isSubmitting: action.payload,
      };

    case 'SET_JOB_STATUS':
      // Save job to localStorage for persistence
      if (action.payload) {
        try {
          localStorage.setItem(STORAGE_KEYS.CURRENT_JOB, JSON.stringify({
            ...action.payload,
            createdAt: action.payload.createdAt.toISOString(),
            completedAt: action.payload.completedAt?.toISOString(),
          }));
        } catch (error) {
          console.warn('Failed to save job to localStorage:', error);
        }
      }
      return {
        ...state,
        currentJob: action.payload,
      };

    case 'SET_ERROR':
      return {
        ...state,
        error: action.payload,
      };

    case 'CLEAR_JOB':
      try {
        localStorage.removeItem(STORAGE_KEYS.CURRENT_JOB);
      } catch (error) {
        console.warn('Failed to clear job from localStorage:', error);
      }
      return {
        ...state,
        currentJob: null,
      };

    // Animation control actions
    case 'TRIGGER_EMAIL_BOUNCE':
      return { ...state, shouldBounceEmail: true };
    
    case 'STOP_EMAIL_BOUNCE':
      return { ...state, shouldBounceEmail: false };
    
    case 'TRIGGER_CATEGORY_HIGHLIGHT':
      return { ...state, shouldHighlightCategories: true };
    
    case 'STOP_CATEGORY_HIGHLIGHT':
      return { ...state, shouldHighlightCategories: false };
    
    case 'TRIGGER_SUBMIT_PULSE':
      return { ...state, shouldPulseSubmit: true };
    
    case 'STOP_SUBMIT_PULSE':
      return { ...state, shouldPulseSubmit: false };

    // HEIC conversion control actions
    case 'SET_HEIC_CONVERSION':
      try {
        localStorage.setItem(STORAGE_KEYS.HEIC_CONVERSION, String(action.payload));
      } catch (error) {
        console.warn('Failed to save HEIC conversion setting to localStorage:', error);
      }
      return { ...state, convertHeicAndHeif: action.payload };
    
    case 'UPDATE_HEIC_FILES_DETECTION':
      return { ...state, hasHeicFiles: action.payload };

    // Purchase credits modal control actions
    case 'SHOW_PURCHASE_MODAL':
      return {
        ...state,
        showPurchaseCreditsModal: true,
        purchaseModalData: action.payload,
      };

    case 'HIDE_PURCHASE_MODAL':
      return {
        ...state,
        showPurchaseCreditsModal: false,
        purchaseModalData: null,
      };

    default:
      return state;
  }
};

/**
 * Determines the file type category for uploaded files
 *
 * @param file - The file to categorize
 * @returns 'image' for image files, 'archive' for ZIP files, 'unknown' otherwise
 *
 * @example
 * ```typescript
 * getFileType(new File([], 'photo.jpg', { type: 'image/jpeg' })) // returns 'image'
 * getFileType(new File([], 'data.zip', { type: 'application/zip' })) // returns 'archive'
 * ```
 */
function getFileType(file: File): 'image' | 'archive' | 'unknown' {
  if (file.type.startsWith('image/')) {
    return 'image';
  }

  if (file.type === 'application/zip' ||
      file.type === 'application/x-zip-compressed' ||
      file.name.toLowerCase().endsWith('.zip')) {
    return 'archive';
  }

  return 'unknown';
}

/**
 * Checks if a file is HEIC/HEIF format
 *
 * Detects HEIC files by MIME type and file extension since browsers
 * may not properly set MIME types for HEIC files.
 *
 * @param file - The file to check
 * @returns true if the file is HEIC/HEIF format
 */
function isHeicFile(file: File): boolean {
  return file.type === 'image/heic' ||
         file.type === 'image/heif' ||
         file.name.toLowerCase().endsWith('.heic') ||
         file.name.toLowerCase().endsWith('.heif');
}

/**
 * Checks if any files in an array are HEIC/HEIF format
 *
 * @param files - Array of files to check
 * @returns true if at least one file is HEIC/HEIF format
 */
function hasHeicFiles(files: File[]): boolean {
  return files.some(isHeicFile);
}

/**
 * AppContext interface
 *
 * Provides centralized state management and action creators for the app.
 * Includes dispatch for advanced use cases (testing, custom actions).
 *
 * @property state - Current application state
 * @property dispatch - Raw dispatch function for testing and advanced use cases
 * @property addFiles - Add files to the upload queue
 * @property removeFile - Remove a file from the queue
 * @property clearQueue - Clear all files from the queue
 * @property setSubmitting - Update submission state
 * @property setJobStatus - Update current job status
 * @property setError - Set error message
 * @property clearJob - Clear current job
 * @property setEmail - Set user email and validity
 * @property setCategories - Set categorization mode and custom categories
 * @property submitJob - Submit files for processing
 * @property restoreUserPreferences - Restore user preferences from localStorage
 * @property startStatusPolling - Start polling for job status updates
 * @property stopStatusPolling - Stop polling for job status updates
 * @property triggerEmailBounce - Trigger email input bounce animation
 * @property stopEmailBounce - Stop email input bounce animation
 * @property triggerCategoryHighlight - Trigger category section highlight
 * @property stopCategoryHighlight - Stop category section highlight
 * @property triggerSubmitPulse - Trigger submit button pulse animation
 * @property stopSubmitPulse - Stop submit button pulse animation
 * @property setHeicConversion - Set HEIC conversion preference
 */
interface AppContextType {
  state: ExtendedAppState;
  /** Raw dispatch function - exposed for testing and advanced use cases */
  dispatch: React.Dispatch<ExtendedActions>;
  addFiles: (files: File[]) => void;
  removeFile: (fileId: string) => void;
  clearQueue: () => void;
  setSubmitting: (submitting: boolean) => void;
  setJobStatus: (job: JobStatus | null) => void;
  setError: (error: string | null) => void;
  clearJob: () => void;
  setEmail: (email: string, isValid: boolean) => void;
  setCategories: (categories: string[], mode: CategoryMode, isValid: boolean) => void;
  submitJob: () => Promise<void>;
  restoreUserPreferences: () => void;
  startStatusPolling: (jobId: string) => void;
  stopStatusPolling: () => void;
  // Animation controls
  triggerEmailBounce: () => void;
  stopEmailBounce: () => void;
  triggerCategoryHighlight: () => void;
  stopCategoryHighlight: () => void;
  triggerSubmitPulse: () => void;
  stopSubmitPulse: () => void;
  // HEIC conversion controls
  setHeicConversion: (enabled: boolean) => void;
  // Purchase credits modal controls
  showPurchaseModal: (fileCount: number, creditsNeeded: number, totalSizeBytes: number) => void;
  hidePurchaseModal: () => void;
  // Credit balance (shared across all consumers)
  creditBalance: number; // Actual balance from server
  displayedCreditBalance: number; // Balance after optimistic deductions
  isCreditsLoading: boolean;
}

// Create context
const AppContext = createContext<AppContextType | undefined>(undefined);

// Initial state
const initialState: ExtendedAppState = {
  fileQueue: [],
  isSubmitting: false,
  currentJob: null,
  error: null,
  lastUpdated: new Date(),
  isEmailValid: false,
  email: '',
  categoryMode: 'predefined',
  customCategories: [],
  areCategoriesValid: true,
  shouldBounceEmail: false,
  shouldHighlightCategories: false,
  shouldPulseSubmit: false,
  convertHeicAndHeif: true, // Default to true as requested
  hasHeicFiles: false,
  showPurchaseCreditsModal: false,
  purchaseModalData: null,
};

// Provider component
export const AppProvider: React.FC<{
  children: ReactNode;
}> = ({ children }) => {
  const [state, dispatch] = useReducer(
    appReducer,
    initialState
  );
  const navigate = useNavigate();
  const toast = useToast();

  // Clerk authentication for credit checking
  const { user } = useUser();

  // Credits hook for actual balance (server source of truth)
  const { balance: actualCreditBalance, isLoading: isCreditsLoading, refetch: refetchCredits } = useCredits();

  // Query pending credits from active/processing jobs (Convex-based optimistic updates)
  // No clerkId needed - automatically uses authenticated user from ctx.auth
  const pendingCreditsQuery = useQuery(
    api.jobs.getUserPendingCredits,
    user ? {} : "skip"
  );
  const pendingCredits = pendingCreditsQuery?.pendingCredits ?? 0;

  // Calculate displayed balance (actual - pending credits from active jobs)
  const displayedCreditBalance = Math.max(0, actualCreditBalance - pendingCredits);

  // Convex mutations for job management
  const createJobMutation = useMutation(api.jobs.createJob);

  // Convex upload service for file uploads
  const { uploadFiles } = useConvexUploadService();

  // Refs to track polling intervals for cleanup (memory leak fix)
  const pollIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const pollTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Track current job ID for status subscription
  const currentJobIdRef = useRef<string | null>(null);

  // Convex query for real-time job status updates (replaces polling)
  const currentJobId = state.currentJob?.id;
  const convexJob = useQuery(
    api.jobs.getJobById,
    currentJobId ? { jobId: currentJobId as Id<"jobs"> } : "skip"
  );

  // Update job status when Convex job changes (reactive subscription)
  useEffect(() => {
    if (!convexJob || !currentJobId) return;

    console.log('📊 Convex job status update:', convexJob);

    // Type assertion: Convex schema includes these fields but TypeScript doesn't infer them
    const job = convexJob as typeof convexJob & {
      processedFiles: number;
      failedFiles: number;
    };

    // Map Convex job status to frontend JobStatus format
    const statusMap = {
      pending: 'pending',
      processing: 'processing',
      completed: 'completed',
      failed: 'failed',
    } as const;

    const mappedStatus: JobStatus = {
      id: currentJobId,
      status: statusMap[job.status] || 'pending',
      message: `${job.processedFiles}/${job.fileCount} files processed`,
      createdAt: new Date(job.createdAt),
      fileCount: job.fileCount,
      processedFiles: job.processedFiles,
      failedFiles: job.failedFiles,
      progress: job.fileCount > 0
        ? Math.round((job.processedFiles / job.fileCount) * 100)
        : 0,
    };

    dispatch({ type: 'SET_JOB_STATUS', payload: mappedStatus });

    // Stop polling when job completes
    if (job.status === 'completed' || job.status === 'failed') {
      dispatch({ type: 'SET_SUBMITTING', payload: false });
      currentJobIdRef.current = null;

      // Refresh credit balance to sync with server (handles refunds for failed files)
      // Convex getUserPendingCredits query will automatically update when job status changes
      console.log('💳 Job completed - refreshing balance to sync with server');
      refetchCredits();

      // Track completion
      analytics.jobCompleted({
        jobId: currentJobId,
        success: job.status === 'completed',
        fileCount: job.fileCount,
        processedFiles: job.processedFiles,
        failedFiles: job.failedFiles,
      });
    }
  }, [convexJob, currentJobId, refetchCredits, dispatch]);

  // Action creators
  const addFiles = useCallback((files: File[]) => {
    dispatch({ type: 'ADD_FILES', payload: files });
    
    // Track file selection analytics
    if (files.length > 0) {
      const totalSizeMB = files.reduce((sum, f) => sum + f.size, 0) / (1024 * 1024);
      const fileTypes = files.map(f => f.type);
      const hasHeic = fileTypes.some(type => type.includes('heic') || type.includes('heif'));
      const hasMixedTypes = new Set(fileTypes).size > 1;
      
      analytics.filesSelected({
        fileCount: files.length,
        totalSizeMB,
        fileTypes,
        hasHeic,
        hasMixedTypes,
        selectionMethod: 'upload'
      });
    }
  }, []);

  const removeFile = useCallback((fileId: string) => {
    dispatch({ type: 'REMOVE_FILE', payload: fileId });
  }, []);

  const clearQueue = useCallback(() => {
    dispatch({ type: 'CLEAR_QUEUE' });
  }, []);

  const setSubmitting = useCallback((submitting: boolean) => {
    dispatch({ type: 'SET_SUBMITTING', payload: submitting });
  }, []);

  const setJobStatus = useCallback((job: JobStatus | null) => {
    dispatch({ type: 'SET_JOB_STATUS', payload: job });
  }, []);

  const setError = useCallback((error: string | null) => {
    dispatch({ type: 'SET_ERROR', payload: error });
  }, []);

  const setEmail = useCallback((email: string, isValid: boolean) => {
    dispatch({ type: 'SET_EMAIL', payload: { email, isValid } });
    
    // Track user identification when email becomes valid
    if (isValid && email) {
      analytics.identifyUser(email);
    }
  }, []);

  const setCategories = useCallback((categories: string[], mode: CategoryMode, isValid: boolean) => {
    dispatch({ type: 'SET_CATEGORIES', payload: { categories, mode, isValid } });
  }, []);

  const restoreUserPreferences = useCallback(() => {
    dispatch({ type: 'RESTORE_USER_PREFERENCES' });
  }, []);

  const clearJob = useCallback(() => {
    dispatch({ type: 'CLEAR_JOB' });
  }, []);

  // Animation control functions
  const triggerEmailBounce = useCallback(() => {
    dispatch({ type: 'TRIGGER_EMAIL_BOUNCE' });
  }, []);

  const stopEmailBounce = useCallback(() => {
    dispatch({ type: 'STOP_EMAIL_BOUNCE' });
  }, []);

  const triggerCategoryHighlight = useCallback(() => {
    dispatch({ type: 'TRIGGER_CATEGORY_HIGHLIGHT' });
  }, []);

  const stopCategoryHighlight = useCallback(() => {
    dispatch({ type: 'STOP_CATEGORY_HIGHLIGHT' });
  }, []);

  const triggerSubmitPulse = useCallback(() => {
    dispatch({ type: 'TRIGGER_SUBMIT_PULSE' });
  }, []);

  const stopSubmitPulse = useCallback(() => {
    dispatch({ type: 'STOP_SUBMIT_PULSE' });
  }, []);

  // HEIC conversion control function
  const setHeicConversion = useCallback((enabled: boolean) => {
    dispatch({ type: 'SET_HEIC_CONVERSION', payload: enabled });
  }, []);

  // Purchase credits modal control functions
  const showPurchaseModal = useCallback((fileCount: number, creditsNeeded: number, totalSizeBytes: number) => {
    dispatch({
      type: 'SHOW_PURCHASE_MODAL',
      payload: { fileCount, creditsNeeded, totalSizeBytes },
    });
  }, []);

  const hidePurchaseModal = useCallback(() => {
    dispatch({ type: 'HIDE_PURCHASE_MODAL' });
  }, []);

  // Stop status polling (memory leak fix)
  const stopStatusPolling = useCallback(() => {
    console.log('🛑 Stopping status polling...');

    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }

    if (pollTimeoutRef.current) {
      clearTimeout(pollTimeoutRef.current);
      pollTimeoutRef.current = null;
    }
  }, []);

  // MIGRATION: Status polling removed - using Convex real-time subscriptions
  // The useQuery hook above provides real-time job status updates via WebSocket
  // This is a no-op function for backward compatibility during migration
  const startStatusPolling = useCallback(async (_jobId: string) => {
    console.log('📡 Using Convex real-time subscription (no polling needed)');
    // Real-time updates handled by useQuery subscription above
  }, []);

  // Enhanced job submission with email integration and extracted utilities
  const submitJob = useCallback(async () => {
    // Step 1: Validate submission requirements (extracted utility)
    const validationError = validateSubmission({
      fileQueue: state.fileQueue,
      isEmailValid: state.isEmailValid,
      isSubmitting: state.isSubmitting,
    });

    if (validationError) {
      setError(validationError.message);
      return;
    }

    try {
      setSubmitting(true);
      setError(null);

      // Step 2: Filter oversized files (extracted utility)
      const filterResult = filterOversizedFiles(state.fileQueue);

      // Show warnings for rejected files
      if (filterResult.rejectedFiles.length > 0) {
        const rejectedNames = formatRejectedFilesMessage(filterResult.rejectedFiles);
        console.warn(`⚠️ ${filterResult.rejectedFiles.length} file(s) rejected due to 10MB size limit:`, rejectedNames);

        if (toast) {
          toast.addToast(
            'error',
            `${filterResult.rejectedFiles.length} file(s) exceed 10MB limit and will be skipped: ${filterResult.rejectedFiles.map(f => f.name).join(', ')}`,
            undefined,
            8000
          );
        }
      }

      // Handle all files rejected scenario
      if (filterResult.allRejected) {
        const errorMessage = getRejectionErrorMessage(filterResult);
        setError(errorMessage || 'All files exceed size limit');
        setSubmitting(false);

        if (toast) {
          toast.addToast('error', 'All selected files are too large', 'Maximum file size is 10MB.', 5000);
        }
        return;
      }

      const { validFiles } = filterResult;

      if (filterResult.rejectedFiles.length > 0) {
        console.log(`📤 Proceeding with ${validFiles.length} valid files, ${filterResult.rejectedFiles.length} rejected`);
      }

      // Step 3: For AUTHENTICATED users: Check credits FIRST (no file/size limits)
      // For UNAUTHENTICATED users: Check free tier limits (10 images/50MB)
      if (user && user.id) {
        // ===== AUTHENTICATED USER PATH =====
        console.log('👤 Authenticated user - checking credits only (no file count/size limits)');

        const creditsNeeded = validFiles.length; // 1 credit per image
        const totalSizeBytes = validFiles.reduce((sum, f) => sum + f.size, 0);

        try {
          console.log(`💳 Checking credits for ${creditsNeeded} images...`);

          // Client-side credit check using actual balance from Convex
          const hasSufficientCredits = actualCreditBalance >= creditsNeeded;

          console.log(`💰 Credit check: need ${creditsNeeded}, have ${actualCreditBalance}, sufficient: ${hasSufficientCredits}`);

          if (!hasSufficientCredits) {
            console.warn(`⚠️ Insufficient credits: need ${creditsNeeded}, have ${actualCreditBalance}`);

            // Show purchase modal with job details
            showPurchaseModal(validFiles.length, creditsNeeded, totalSizeBytes);

            // Stop submission flow
            setSubmitting(false);
            return;
          }

          console.log(`✅ Credit check passed: ${actualCreditBalance} credits available`);
          // Continue to job submission below
        } catch (error) {
          console.error('❌ Error checking credits:', error);

          // For credit check errors, show modal anyway (fail safe)
          showPurchaseModal(validFiles.length, creditsNeeded, totalSizeBytes);
          setSubmitting(false);
          return;
        }
      } else {
        // ===== UNAUTHENTICATED USER PATH =====
        console.log('🔓 Unauthenticated user - checking free tier limits (10 images/50MB)');

        const limitCheck = checkLimits(validFiles);

        if (limitCheck.exceedsLimit) {
          console.warn(`⚠️ Free tier limit exceeded: ${limitCheck.reason} - attempted: ${limitCheck.attempted}, limit: ${limitCheck.limit}`);
          await handleLimitExceeded(validFiles, navigate, limitCheck);
          setSubmitting(false);
          return;
        }

        console.log(`✅ Free tier limit check passed: ${validFiles.length} images within limits`);
        // Continue to job submission below
      }

      console.log(`🚀 Submitting ${validFiles.length} files with email: ${state.email}`);

      // Track upload start analytics
      const startTime = Date.now();
      analytics.uploadStarted({
        jobId: 'pending',
        fileCount: validFiles.length,
        totalSizeMB: validFiles.reduce((sum, f) => sum + f.size, 0) / (1024 * 1024),
        hasCustomCategories: state.categoryMode !== 'predefined',
        email: state.email
      });

      // Step 4: Prepare custom categories (extracted utility)
      const customCategoriesData = prepareCustomCategories({
        categoryMode: state.categoryMode,
        customCategories: state.customCategories,
      });

      // Step 5: Create job in Convex
      console.log('🚀 Creating Convex job...');

      // Determine if this is a preview (free tier) job for unauthenticated users
      const isPreviewJob = !user?.id && validFiles.length <= 10;

      if (isPreviewJob) {
        console.log('🎁 Creating preview job for unauthenticated user (no credit deduction)');
      }

      const jobId = await createJobMutation({
        userId: user?.id || 'anonymous',
        clerkId: user?.id || `preview_${state.email}`, // Special clerkId format for preview jobs
        email: state.email,
        fileCount: validFiles.length,
        totalSizeBytes: validFiles.reduce((sum, f) => sum + f.size, 0),
        categoryMode: state.categoryMode,
        customCategories: customCategoriesData || undefined, // Convert null to undefined for Convex
        convertHeicAndHeif: state.convertHeicAndHeif,
        isPreviewJob, // Flag to skip credit deduction
      });

      console.log('✅ Job created:', jobId);

      // Convex getUserPendingCredits query will automatically show optimistic balance
      // by querying active/processing jobs and deducting their file counts

      // Step 6: Upload files to Convex storage
      console.log(`📤 Uploading ${validFiles.length} files to Convex storage...`);
      await uploadFiles(
        validFiles,
        jobId,
        state.convertHeicAndHeif,
        (completed, total) => {
          console.log(`📦 Upload progress: ${completed}/${total} files`);
        }
      );

      console.log('✅ All files uploaded successfully');

      // Track successful upload completion
      analytics.uploadCompleted({
        jobId: jobId as string,
        success: true,
        processingTimeMs: Date.now() - startTime
      });

      // Create initial job status
      const initialJob: JobStatus = {
        id: jobId as string,
        status: 'pending',
        message: 'Files uploaded successfully, processing started',
        createdAt: new Date(),
        fileCount: validFiles.length,
      };

      setJobStatus(initialJob);
      currentJobIdRef.current = jobId as string;

      // Clear the file queue after successful submission
      clearQueue();

      // Processing starts automatically via Convex scheduler
      // Real-time status updates via useQuery subscription above
      console.log('✅ Job submitted - processing will start automatically');
      console.log('📡 Real-time status updates active via Convex subscription');

    } catch (error) {
      console.error('❌ Error submitting job:', error);
      setError(error instanceof Error ? error.message : 'Failed to submit job');
      setSubmitting(false);
    }
  }, [
    state.fileQueue,
    state.email,
    state.isEmailValid,
    state.isSubmitting,
    state.categoryMode,
    state.customCategories,
    state.convertHeicAndHeif,
    setSubmitting,
    setJobStatus,
    setError,
    clearQueue,
    navigate,
    toast,
    user,
    actualCreditBalance,
    showPurchaseModal,
    createJobMutation,
    uploadFiles,
  ]);

  // MIGRATION: Cleanup effect no longer needed - Convex subscriptions auto-cleanup
  // useQuery automatically unsubscribes when component unmounts

  const contextValue: AppContextType = {
    state,
    dispatch, // Expose dispatch for testing and advanced use cases
    addFiles,
    removeFile,
    clearQueue,
    setSubmitting,
    setJobStatus,
    setError,
    clearJob,
    setEmail,
    setCategories,
    submitJob,
    restoreUserPreferences,
    startStatusPolling,
    stopStatusPolling,
    // Animation controls
    triggerEmailBounce,
    stopEmailBounce,
    triggerCategoryHighlight,
    stopCategoryHighlight,
    triggerSubmitPulse,
    stopSubmitPulse,
    setHeicConversion,
    // Purchase credits modal controls
    showPurchaseModal,
    hidePurchaseModal,
    // Credit balance (shared across all components)
    creditBalance: actualCreditBalance,
    displayedCreditBalance,
    isCreditsLoading,
  };

  return (
    <AppContext.Provider value={contextValue}>
      {children}
    </AppContext.Provider>
  );
};

// Hook to use the context
export const useApp = (): AppContextType => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useApp must be used within an AppProvider');
  }
  return context;
};

// Backwards compatibility - export file queue specific functions
export const useFileQueue = () => {
  const { state, addFiles, removeFile, clearQueue, setSubmitting, setJobStatus, setError, clearJob } = useApp();
  return {
    state,
    addFiles,
    removeFile,
    clearQueue,
    setSubmitting,
    setJobStatus,
    setError,
    clearJob,
  };
};

// Optimized hook that only subscribes to file queue changes
export const useFileQueueOnly = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useFileQueueOnly must be used within an AppProvider');
  }
  
  // Use useMemo to create a stable object that only changes when fileQueue changes
  return React.useMemo(() => ({
    fileQueue: context.state.fileQueue,
    removeFile: context.removeFile,
  }), [context.state.fileQueue, context.removeFile]);
};